/* eslint-disable */
import { Bar, BarChart, CartesianGrid, Cell, Label, Line, LineChart, Pie, PieChart, XAxis, YAxis } from 'recharts'
import { Download, FileText } from 'lucide-react'

import { Avatar, AvatarFallback } from '../../Components/ui/avatar'
import { Button } from '../../Components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../../Components/ui/chart'
import { ProgressTwo } from '../../Components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../Components/ui/table'
import { cn } from '../../lib/utils'
import {
  formatCompactRupee,
  formatDisplayDate,
  formatGapClosureMoment,
  formatGapYears,
  formatRupee,
} from './firstUserIdeaUtils'

const EmptyValue = ({ children = 'Not provided' }) => (
  <span className='text-sm text-[var(--fb-text-muted)]'>{children}</span>
)

const SectionCard = ({ title, description, children, className = '' }) => (
  <Card className={cn('border border-[var(--fb-border)] shadow-[var(--fb-shadow-sm)]', className)}>
    <CardHeader className='gap-1 p-4 md:p-6'>
      <CardTitle className='font-IBM text-base font-semibold tracking-tight text-[var(--fb-text-strong)] md:text-lg'>
        {title}
      </CardTitle>
      {description ? <CardDescription>{description}</CardDescription> : null}
    </CardHeader>
    <CardContent className='p-4 pt-0 md:p-6 md:pt-0'>{children}</CardContent>
  </Card>
)

const getInitials = (name) =>
  String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'IN'

const chartConfigFromRows = (rows, dataKey = 'amount') =>
  rows.reduce(
    (config, row) => {
      config[row.name] = { label: row.name, color: row.fill }
      return config
    },
    { [dataKey]: { label: 'Amount' } },
  )

const rupeeTooltip = (value, name) => (
  <div className='flex flex-1 items-center justify-between gap-8'>
    <span className='text-[var(--fb-text-muted)]'>{name}</span>
    <span className='font-medium tabular-nums text-[var(--fb-text-strong)]'>
      {formatRupee(value) || '₹0'}
    </span>
  </div>
)

const ColoredBarChart = ({ rows }) => {
  if (!rows?.length) return <EmptyValue>Not enough data to chart.</EmptyValue>

  return (
    <ChartContainer config={chartConfigFromRows(rows)} className='aspect-auto h-[240px] w-full'>
      <BarChart data={rows} margin={{ left: 8, right: 8, top: 8 }}>
        <CartesianGrid vertical={false} stroke='var(--fb-border)' />
        <XAxis dataKey='name' tickLine={false} axisLine={false} tickMargin={8} />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={56}
          tickFormatter={(value) => formatCompactRupee(value)}
        />
        <ChartTooltip cursor={false} content={<ChartTooltipContent formatter={rupeeTooltip} />} />
        <Bar dataKey='amount' name='Amount' radius={[8, 8, 0, 0]} maxBarSize={64}>
          {rows.map((row) => (
            <Cell key={row.name} fill={row.fill} />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  )
}

const ExpenseDonut = ({ rows, centerLabel, centerValue }) => {
  if (!rows?.length) return <EmptyValue>No spending split to show.</EmptyValue>

  return (
    <ChartContainer
      config={chartConfigFromRows(rows)}
      className='mx-auto aspect-square max-h-[280px]'
    >
      <PieChart>
        <ChartTooltip content={<ChartTooltipContent hideLabel formatter={rupeeTooltip} />} />
        <Pie
          data={rows}
          dataKey='amount'
          nameKey='name'
          innerRadius={68}
          strokeWidth={4}
          paddingAngle={3}
          cornerRadius={8}
        >
          {rows.map((row) => (
            <Cell key={row.name} fill={row.fill} />
          ))}
          <Label
            content={({ viewBox }) => {
              if (!viewBox || !('cx' in viewBox)) return null
              return (
                <text x={viewBox.cx} y={viewBox.cy} textAnchor='middle' dominantBaseline='middle'>
                  <tspan
                    x={viewBox.cx}
                    y={viewBox.cy}
                    className='fill-[var(--fb-text-strong)] text-lg font-bold'
                  >
                    {centerValue}
                  </tspan>
                  <tspan
                    x={viewBox.cx}
                    y={(viewBox.cy || 0) + 18}
                    className='fill-[var(--fb-text-muted)] text-[10px]'
                  >
                    {centerLabel}
                  </tspan>
                </text>
              )
            }}
          />
        </Pie>
        <ChartLegend
          content={<ChartLegendContent nameKey='name' />}
          className='flex-wrap gap-2 pt-2 [&>*]:justify-center'
        />
      </PieChart>
    </ChartContainer>
  )
}

const ProfileHeader = ({ view, onDownloadCsv, onDownloadReport, csvDisabled }) => (
  <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
    <div className='flex min-w-0 items-start gap-3'>
      <Avatar className='h-12 w-12 bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'>
        <AvatarFallback className='bg-[var(--fb-accent-soft)] text-sm font-semibold text-[var(--fb-accent-text)]'>
          {getInitials(view.fullName)}
        </AvatarFallback>
      </Avatar>
      <div className='flex min-w-0 flex-col gap-2'>
        <div>
          <p className='fabits-eyebrow'>Questionnaire v{view.version}</p>
          <h2 className='font-IBM text-xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-2xl'>
            {view.fullName}
          </h2>
          <p className='text-sm text-[var(--fb-text-muted)]'>Household snapshot and plan results</p>
        </div>
        <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--fb-text-muted)]'>
          {view.phoneNumber ? <span>{view.phoneNumber}</span> : null}
          <span>Submitted {formatDisplayDate(view.submittedOn) || 'Not provided'}</span>
          <span>Last updated {formatDisplayDate(view.updatedOn) || 'Not provided'}</span>
        </div>
      </div>
    </div>

    <div className='flex flex-wrap gap-2'>
      <Button variant='outline' size='sm' className='rounded-xl' onClick={onDownloadReport}>
        <FileText className='h-4 w-4' />
        Download report
      </Button>
      <Button size='sm' className='rounded-xl' onClick={onDownloadCsv} disabled={csvDisabled}>
        <Download className='h-4 w-4' />
        Download CSV
      </Button>
    </div>
  </div>
)

const Snapshot = ({ metrics }) => (
  <SectionCard title='Plan snapshot' description='Key numbers derived from the questionnaire.'>
    <div className='grid grid-cols-2 gap-3 lg:grid-cols-3'>
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className='flex min-h-[72px] flex-col justify-center gap-1 rounded-md bg-[var(--fb-surface-muted)] px-4 py-3'
        >
          <p className='fabits-eyebrow'>{metric.label}</p>
          <p className='text-base font-semibold text-[var(--fb-text-strong)] md:text-lg'>
            {metric.value || <EmptyValue />}
          </p>
        </div>
      ))}
    </div>
  </SectionCard>
)

const FormulaNote = ({ children }) =>
  children ? (
    <p className='mt-1 text-xs leading-5 text-[var(--fb-text-muted)]'>{children}</p>
  ) : null

const DetailRow = ({ label, value, formula }) => (
  <div className='py-2'>
    <div className='flex items-start justify-between gap-4'>
      <p className='text-sm text-[var(--fb-text-muted)]'>{label}</p>
      <p className='text-right text-sm font-medium text-[var(--fb-text-strong)]'>
        {value || <EmptyValue />}
      </p>
    </div>
    <FormulaNote>{formula}</FormulaNote>
  </div>
)

const TotalRow = ({ value, label = 'Total', formula }) => (
  <div className='border-t-2 border-[var(--fb-border-strong)] py-2'>
    <div className='flex items-start justify-between gap-4'>
      <p className='text-sm font-semibold text-[var(--fb-text-strong)]'>{label}</p>
      <p className='text-right text-sm font-semibold text-[var(--fb-text-strong)]'>
        {formatRupee(value) || '₹0'}
      </p>
    </div>
    <FormulaNote>{formula}</FormulaNote>
  </div>
)

const gapFormulas = (estimate) => {
  const inflation = estimate.assumptions.essentialInflation
  const goalInflation = estimate.assumptions.nonEssentialInflation
  const educationInflation = estimate.assumptions.educationInflation
  const planningReturn = estimate.assumptions.hybridReturn
  const investReturn = estimate.assumptions.gapInvestmentReturn
  const age = estimate.currentAge
  const planTill = estimate.assumptions.planTill

  return {
    retirement: `Each year from age ${age} to ${planTill - 1}: ((monthly retirement spend + yearly total ÷ 12) × 12) × (1 + ${inflation}%)^years ÷ (1 + ${planningReturn}%)^years`,
    goals: `Each goal: target × (1 + ${goalInflation}%)^years ÷ (1 + ${planningReturn}%)^years. Years = target year − this year.`,
    children: `Annual school fees grown at ${educationInflation}% and discounted at ${planningReturn}% from the child’s age until age 25, for at most 18 years`,
    parents: `Annual parent support grown at ${inflation}% and discounted at ${planningReturn}% from age ${age} through age ${planTill - 1}. ₹0 if neither parent is supported.`,
    emergency: 'Monthly expenses × 6',
    contingency:
      '10% of retirement, goals, education and parent support. At least ₹50 lakh, rounded up in ₹25 lakh steps.',
    replacement:
      '10% of retirement, goals, education and parent support. At least ₹50 lakh, rounded up in ₹25 lakh steps.',
    total:
      'Retirement + one-time goals + children’s education + parent support + emergency + contingency + asset replacement',
    holdings: 'Sum of all holding values',
    loans: 'Sum of outstanding loan amounts',
    netWorth: 'Total holdings − outstanding loans',
    gap: 'Estimated target today − current net worth. ₹0 when net worth is already higher.',
    gross: 'Sum of monthly income before deductions',
    deductions: 'Tax + PF + NPS on salary income only',
    netIncome: 'Income before deductions − tax, PF and NPS, floored at ₹0',
    monthlyExpenses:
      'Sum of monthly costs + loan EMIs. A home-loan EMI is left out when housing EMI is already entered.',
    annualMonthly: '(Lifestyle + selected annual costs + extra yearly costs) ÷ 12',
    surplus:
      'Income after deductions − monthly expenses − annual costs spread monthly, floored at ₹0',
    inflatedGap: `Gap today × (1 + ${inflation}%)^years to close`,
    time: `Smallest time where the future value of the monthly surplus at ${investReturn}% a year reaches the gap grown at ${inflation}% a year`,
    closure: 'Today plus the months to close. Age is taken from the date of birth on that date.',
    requiredLine: `Gap today × (1 + ${inflation}%)^year`,
    investedLine: `Future value of the monthly surplus at ${investReturn}% a year, capped at that year’s required gap`,
  }
}

const gapChartConfig = {
  required: { label: 'Required gap', color: 'var(--fb-vulcan-400)' },
  invested: { label: 'Invested surplus', color: 'var(--fb-accent)' },
}

const GapChart = ({ estimate }) => {
  const points = estimate?.gapProjection || []
  if (!points.length) return <EmptyValue>Not enough data to chart.</EmptyValue>

  const rows = points.map((point) => ({
    year: point.year,
    required: Math.round(point.requiredAmount),
    invested: Math.round(point.investedAmount),
  }))

  return (
    <ChartContainer config={gapChartConfig} className='aspect-auto h-[260px] w-full'>
      <LineChart data={rows} margin={{ left: 8, right: 8, top: 8 }}>
        <CartesianGrid vertical={false} stroke='var(--fb-border)' />
        <XAxis
          dataKey='year'
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => (Number(value) === 0 ? 'Now' : `${value}y`)}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={56}
          tickFormatter={(value) => formatCompactRupee(value)}
        />
        <ChartTooltip cursor={false} content={<ChartTooltipContent formatter={rupeeTooltip} />} />
        <ChartLegend content={<ChartLegendContent />} />
        <Line
          type='monotone'
          dataKey='required'
          name='Required gap'
          stroke='var(--color-required)'
          strokeDasharray='6 4'
          dot={false}
          strokeWidth={2}
        />
        <Line
          type='monotone'
          dataKey='invested'
          name='Invested surplus'
          stroke='var(--color-invested)'
          dot={{ r: 3 }}
          strokeWidth={2}
        />
      </LineChart>
    </ChartContainer>
  )
}

const QuestionnaireV2Profile = ({ view, onDownloadCsv, onDownloadReport, csvDisabled }) => {
  const closureMoment = formatGapClosureMoment(view.estimate)
  const formulas = view.estimate ? gapFormulas(view.estimate) : null
  return (
    <div className='flex flex-col gap-4 md:gap-6'>
      <ProfileHeader
        view={view}
        onDownloadCsv={onDownloadCsv}
        onDownloadReport={onDownloadReport}
        csvDisabled={csvDisabled}
      />

      <Snapshot metrics={view.metrics} />

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6'>
        <SectionCard title='Monthly cash flow' description='Income versus all recurring costs.'>
          <ColoredBarChart rows={view.cashflowChart} />
        </SectionCard>
        <SectionCard
          title='Where money goes'
          description='Monthly split, with yearly costs shown as a monthly equivalent.'
        >
          <ExpenseDonut
            rows={view.expenseChart}
            centerLabel='monthly outgo'
            centerValue={formatCompactRupee(view.totals.totalMonthlyOutflow)}
          />
        </SectionCard>
      </div>

      <SectionCard title='Family'>
        {view.members.length ? (
          <div className='overflow-x-auto'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Relationship</TableHead>
                  <TableHead>Date of birth</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Occupation</TableHead>
                  <TableHead>Dependent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {view.members.map((member) => (
                  <TableRow key={member.id || member.name}>
                    <TableCell className='font-medium text-[var(--fb-text-strong)]'>
                      {member.name || '—'}
                    </TableCell>
                    <TableCell>{member.relationship || '—'}</TableCell>
                    <TableCell>{member.dobLabel || '—'}</TableCell>
                    <TableCell>
                      {member.age !== null && member.age !== undefined ? member.age : '—'}
                    </TableCell>
                    <TableCell>{member.occupation || '—'}</TableCell>
                    <TableCell>{member.dependent ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyValue />
        )}
      </SectionCard>

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6'>
        <SectionCard title='Income'>
          {view.salaries.length ? (
            <div>
              <div className='flex flex-col divide-y divide-[var(--fb-border)]'>
                {view.salaries.map((salary) => (
                  <div
                    key={salary.id || salary.incomeType}
                    className='flex flex-col gap-1 py-3 first:pt-0 last:pb-0'
                  >
                    <div className='flex items-center justify-between gap-3'>
                      <p className='text-sm font-medium text-[var(--fb-text-strong)]'>
                        {salary.incomeType || 'Income'}
                      </p>
                      <p className='text-sm font-semibold text-[var(--fb-text-strong)]'>
                        {formatRupee(salary.grossAmount) || <EmptyValue />}
                      </p>
                    </div>
                    <p className='text-xs text-[var(--fb-text-muted)]'>
                      {[
                        salary.memberName ? salary.memberName : null,
                        salary.pfAmount ? `PF ${formatRupee(salary.pfAmount)}` : null,
                        salary.npsAmount ? `NPS ${formatRupee(salary.npsAmount)}` : null,
                        salary.taxAmount ? `Tax ${formatRupee(salary.taxAmount)}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'No PF / NPS / tax captured'}
                    </p>
                  </div>
                ))}
              </div>
              <TotalRow value={view.totals.monthlyIncome} />
            </div>
          ) : (
            <EmptyValue />
          )}
        </SectionCard>

        <SectionCard title='Spending'>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div>
              <p className='mb-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--fb-text-muted)]'>
                Monthly
              </p>
              {view.monthlyExpenseRows.length || view.parentsSupport ? (
                <div>
                  <div className='divide-y divide-[var(--fb-border)]'>
                    {view.monthlyExpenseRows.map((row) => (
                      <DetailRow key={row.key} label={row.label} value={formatRupee(row.amount)} />
                    ))}
                    {view.parentsSupport ? (
                      <DetailRow label='Parents support' value={view.parentsSupport} />
                    ) : null}
                  </div>
                  {view.monthlyExpenseRows.length ? (
                    <TotalRow value={view.totals.monthlyExpensesTotal} />
                  ) : null}
                </div>
              ) : (
                <EmptyValue />
              )}
            </div>
            <div>
              <p className='mb-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--fb-text-muted)]'>
                Yearly
              </p>
              {view.yearlyExpenseRows.length ? (
                <div>
                  <div className='divide-y divide-[var(--fb-border)]'>
                    {view.yearlyExpenseRows.map((row) => (
                      <DetailRow
                        key={row.key}
                        label={row.label}
                        value={row.missing ? 'Selected, no amount' : formatRupee(row.amount)}
                      />
                    ))}
                  </div>
                  <TotalRow value={view.totals.yearlyExpensesTotal} />
                </div>
              ) : (
                <EmptyValue />
              )}
            </div>
          </div>
        </SectionCard>
      </div>

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6'>
        <SectionCard title='Holdings'>
          {view.holdings.length ? (
            <div>
              <div className='flex flex-col divide-y divide-[var(--fb-border)]'>
                {view.holdings.map((holding) => (
                  <div
                    key={holding.id || holding.name}
                    className='flex items-start justify-between gap-4 py-2'
                  >
                    <div>
                      <p className='text-sm text-[var(--fb-text-muted)]'>
                        {holding.name || 'Holding'}
                      </p>
                      {holding.location ? (
                        <p className='text-xs text-[var(--fb-text-muted)]'>{holding.location}</p>
                      ) : null}
                    </div>
                    <p className='text-right text-sm font-medium text-[var(--fb-text-strong)]'>
                      {formatRupee(holding.valueAmount) || <EmptyValue />}
                    </p>
                  </div>
                ))}
              </div>
              <TotalRow value={view.totals.holdingsTotal} />
            </div>
          ) : (
            <EmptyValue />
          )}
        </SectionCard>

        <SectionCard
          title='Holdings vs goals'
          description='Current holdings and earmarked savings against listed goal targets.'
        >
          <ColoredBarChart rows={view.assetsChart} />
        </SectionCard>

        <SectionCard title='Goals'>
          {view.goals.length ? (
            <div>
              <div className='flex flex-col gap-4'>
                {view.goals.map((goal) => (
                  <div key={goal.id || goal.name} className='flex flex-col gap-2'>
                    <div className='flex items-start justify-between gap-3'>
                      <div>
                        <p className='text-sm font-medium text-[var(--fb-text-strong)]'>
                          {goal.name || goal.kind || 'Goal'}
                        </p>
                        <p className='text-xs text-[var(--fb-text-muted)]'>
                          {[
                            goal.kind,
                            goal.year,
                            goal.yearsAway !== null ? `${goal.yearsAway} yrs away` : null,
                            goal.child ? `For ${goal.child}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                      <p className='text-sm font-semibold text-[var(--fb-text-strong)]'>
                        {formatRupee(goal.amount) || <EmptyValue />}
                      </p>
                    </div>
                    {goal.fundedPercent !== null ? (
                      <div className='flex flex-col gap-1'>
                        <ProgressTwo value={goal.fundedPercent} />
                        <p className='text-xs text-[var(--fb-text-muted)]'>
                          Saved {formatRupee(goal.saved) || '₹0'}
                          {goal.remaining ? ` · ${formatRupee(goal.remaining)} remaining` : ''}
                        </p>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              <TotalRow value={view.totals.goalsTotal} />
            </div>
          ) : (
            <EmptyValue />
          )}
        </SectionCard>

        <SectionCard title='Loans'>
          {view.loans.length > 0 ? (
            <div>
              <div className='flex flex-col divide-y divide-[var(--fb-border)]'>
                {view.loans.map((loan, index) => (
                  <div
                    key={loan.id || loan.name || index}
                    className='flex items-start justify-between gap-4 py-2'
                  >
                    <div className='min-w-0'>
                      <p className='text-sm text-[var(--fb-text-muted)]'>
                        {loan.name || loan.type || `Loan ${index + 1}`}
                      </p>
                      {loan.remark ? (
                        <p className='text-xs text-[var(--fb-text-muted)]'>{loan.remark}</p>
                      ) : null}
                    </div>
                    <p className='text-right text-sm font-medium text-[var(--fb-text-strong)]'>
                      {[
                        loan.outstandingAmount ? formatRupee(loan.outstandingAmount) : null,
                        loan.emiAmount ? `${formatRupee(loan.emiAmount)} EMI` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </p>
                  </div>
                ))}
              </div>
              <TotalRow value={view.totals.loansOutstanding} />
            </div>
          ) : (
            <EmptyValue>No loans reported</EmptyValue>
          )}
        </SectionCard>
      </div>

      <SectionCard title='Protection'>
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
          {view.insuranceItems.map((item) => (
            <div
              key={item.key}
              className='flex flex-col gap-2 rounded-md border border-[var(--fb-border)] px-4 py-3'
            >
              <p className='text-sm font-medium text-[var(--fb-text-strong)]'>{item.label}</p>
              <p className='text-sm text-[var(--fb-text-muted)]'>
                {item.covered ? 'Covered' : item.declined ? 'No' : 'Not provided'}
              </p>
              {item.coverageAmount || item.coverage || item.who || item.basis ? (
                <p className='text-xs text-[var(--fb-text-muted)]'>
                  {[item.who, formatRupee(item.coverageAmount) || item.coverage, item.basis]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </SectionCard>

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6'>
        <SectionCard title='Retirement'>
          <div className='divide-y divide-[var(--fb-border)]'>
            <DetailRow label='Lifestyle' value={view.retirement.style} />
            <DetailRow label='Monthly spend' value={formatRupee(view.retirement.monthly)} />
            <DetailRow
              label='Monthly increase'
              value={
                view.retirement.monthlyIncrease !== undefined &&
                  view.retirement.monthlyIncrease !== ''
                  ? `${view.retirement.monthlyIncrease}%`
                  : null
              }
            />
            <DetailRow
              label='Estimate confirmed'
              value={view.retirement.estimateConfirmed ? 'Yes' : 'No'}
            />
          </div>
        </SectionCard>

        <SectionCard
          title='Lifestyle today vs retirement'
          description='Current monthly outgo compared with planned retirement spend.'
        >
          <ColoredBarChart rows={view.retirementChart} />
        </SectionCard>

        <SectionCard title='Assumptions'>
          {view.assumptions.length ? (
            <div className='grid grid-cols-2 gap-3'>
              {view.assumptions.map((item) => (
                <div key={item.key} className='rounded-md bg-[var(--fb-surface-muted)] px-3 py-2'>
                  <p className='text-xs text-[var(--fb-text-muted)]'>{item.label}</p>
                  <p className='text-sm font-semibold text-[var(--fb-text-strong)]'>{item.value}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyValue />
          )}
        </SectionCard>
      </div>

      {view.estimate ? (
        <>
          <SectionCard
            title='Gap analysis'
            description={`Surplus catches the inflated gap in ${formatGapYears(
              view.estimate.gapClosureYears,
            )}. This plots the required amount growing at ${
              view.estimate.assumptions.essentialInflation
            }% inflation against monthly surplus invested at ${
              view.estimate.assumptions.gapInvestmentReturn
            }% yearly return.${closureMoment ? ` Around ${closureMoment}.` : ''}`}
          >
            <GapChart estimate={view.estimate} />
            <div className='mt-3'>
              <FormulaNote>Required gap = {formulas.requiredLine}</FormulaNote>
              <FormulaNote>Invested surplus = {formulas.investedLine}</FormulaNote>
            </div>
          </SectionCard>

          <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6'>
            <SectionCard
              title='Estimated target today'
              description={`Retirement spending is funded from age ${view.estimate.currentAge} through age ${
                view.estimate.assumptions.planTill - 1
              }, using the lifestyle amount entered.`}
            >
              <div className='divide-y divide-[var(--fb-border)]'>
                {view.estimate.breakdown.map((item) => (
                  <DetailRow
                    key={item.key}
                    label={item.label}
                    value={formatRupee(item.value)}
                    formula={formulas[item.key]}
                  />
                ))}
              </div>
              <TotalRow value={view.estimate.totalRequiredToday} formula={formulas.total} />
            </SectionCard>

            <SectionCard
              title='What they’ve already built'
              description='Includes all entered holdings, including property and gold. Availability to fund goals still needs review.'
            >
              <div className='divide-y divide-[var(--fb-border)]'>
                <DetailRow
                  label='Total holdings'
                  value={formatRupee(view.estimate.holdings)}
                  formula={formulas.holdings}
                />
                <DetailRow
                  label='Less outstanding loans'
                  value={formatRupee(view.estimate.loans)}
                  formula={formulas.loans}
                />
              </div>
              <TotalRow
                label='Net worth'
                value={view.estimate.currentNetWorth}
                formula={formulas.netWorth}
              />
            </SectionCard>

            <SectionCard
              title='The distance to the target'
              description='Compares the target with total net worth today. Future savings and investment growth are not deducted here.'
            >
              <div className='divide-y divide-[var(--fb-border)]'>
                <DetailRow
                  label='Estimated target today'
                  value={formatRupee(view.estimate.totalRequiredToday)}
                  formula={formulas.total}
                />
                <DetailRow
                  label='Less current net worth'
                  value={formatRupee(view.estimate.currentNetWorth)}
                  formula={formulas.netWorth}
                />
              </div>
              <TotalRow label='Remaining gap' value={view.estimate.gapToday} formula={formulas.gap} />
            </SectionCard>

            <SectionCard title='Room in the monthly budget'>
              <div className='divide-y divide-[var(--fb-border)]'>
                <DetailRow
                  label='Income before deductions'
                  value={formatRupee(view.estimate.monthlyGross)}
                  formula={formulas.gross}
                />
                <DetailRow
                  label='Tax, PF & NPS entered'
                  value={formatRupee(view.estimate.monthlyDeductions)}
                  formula={formulas.deductions}
                />
                <DetailRow
                  label='Income after deductions'
                  value={formatRupee(view.estimate.monthlyNetIncome)}
                  formula={formulas.netIncome}
                />
                <DetailRow
                  label='Monthly expenses'
                  value={formatRupee(view.estimate.monthlyExpenseTotal)}
                  formula={formulas.monthlyExpenses}
                />
                <DetailRow
                  label='Annual costs, spread monthly'
                  value={formatRupee(view.estimate.annualExpenseTotal / 12)}
                  formula={formulas.annualMonthly}
                />
              </div>
              <TotalRow
                label='Monthly surplus'
                value={view.estimate.monthlySurplus}
                formula={formulas.surplus}
              />
            </SectionCard>

            <SectionCard
              title='Estimated time to close the gap'
              description='Assumes the monthly surplus is invested every month while the required amount keeps rising with inflation.'
            >
              <div className='divide-y divide-[var(--fb-border)]'>
                <DetailRow
                  label='Gap today'
                  value={formatRupee(view.estimate.gapToday)}
                  formula={formulas.gap}
                />
                <DetailRow
                  label='Inflated gap by then'
                  value={formatRupee(view.estimate.futureGapTarget || 0)}
                  formula={formulas.inflatedGap}
                />
                <DetailRow
                  label='Monthly surplus invested'
                  value={formatRupee(view.estimate.monthlySurplus)}
                  formula={formulas.surplus}
                />
                <DetailRow
                  label='Inflation on requirement'
                  value={`${view.estimate.assumptions.essentialInflation}%`}
                />
                <DetailRow
                  label='Assumed yearly return'
                  value={`${view.estimate.assumptions.gapInvestmentReturn}%`}
                />
                <DetailRow
                  label='Estimated time'
                  value={formatGapYears(view.estimate.gapClosureYears)}
                  formula={formulas.time}
                />
                {closureMoment ? (
                  <DetailRow
                    label='When this happens'
                    value={closureMoment}
                    formula={formulas.closure}
                  />
                ) : null}
              </div>
            </SectionCard>
          </div>

          <p className='text-xs leading-5 text-[var(--fb-text-muted)]'>
            A first estimate, with room to refine. Based on a {view.estimate.assumptions.hybridReturn}%
            planning return, {view.estimate.assumptions.essentialInflation}% essential-cost inflation
            and a plan through age {view.estimate.assumptions.planTill}. The time estimate compares
            the inflation-adjusted requirement with monthly surplus invested at{' '}
            {view.estimate.assumptions.gapInvestmentReturn}% yearly return. Unanswered amounts are
            not included.
          </p>
        </>
      ) : null}
    </div>
  )
}

export default QuestionnaireV2Profile
